import { useState, useEffect, useRef } from 'react';
import { collection, addDoc, serverTimestamp, doc, getDoc, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { addTradeOffer } from './AddTradeOffer';
import { useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import { UserContext } from '../contexts/UserContext';
import { MembershipContext } from '../contexts/MembershipContext';
import { formatCurrencyInput } from '../utils/currencyUtils';
import { MAX_INITIATOR_PEDALS } from '../utils/tradeUtils';
import { buildPedalSnapshotsPayload } from '../utils/tradeOfferPedalSnapshots';
import { recordOfferIntentEvent, OFFER_INTENT_KINDS } from '../utils/offerIntentAnalytics';
import { createOfferSubmitTimer } from '../utils/offerSubmitTiming';
import { maybePromptForPush } from '../services/softPushPrompt';
import AddressRequiredModal from './AddressRequiredModal';
import { usePedalMarketValues } from '../hooks/usePedalMarketValues';
import { getDisplayTier, formatDollarsFromCents } from '../utils/reverbPricing';
import InactiveOwnerNotice from './InactiveOwnerNotice';
import { placeholderFor, SKETCH_PLACEHOLDER_URL } from '../utils/listingPlaceholder';
import { allowsTrade, offerModeOf } from '../utils/offerMode';


const TradeOfferForm = ({ matchId, fromPedalId, toPedalId, toOwnerId, onCancel, onAdditionalPedalsChange, ownerName, ownerLastActiveWeek }) => {
  const [offerType, setOfferType] = useState('');
  const [cashAmount, setCashAmount] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  // The inactive-owner notice points its tail at this button.
  const submitButtonRef = useRef(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [showCostBreakdown, setShowCostBreakdown] = useState(false);
  const [costBreakdown, setCostBreakdown] = useState(null);
  const [calculatingCosts, setCalculatingCosts] = useState(false);
  const navigate = useNavigate();
  const { shippingAddress, loading: userLoading, refreshUserData } = useContext(UserContext);
  const userContext = useContext(UserContext);
  const { membershipTier, getCashFeePercentage: getMembershipCashFeePercentage, getTradeCompletionFee } = useContext(MembershipContext);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showRefreshButton, setShowRefreshButton] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);

  const [additionalPedalIds, setAdditionalPedalIds] = useState([]);
  const [showAddMorePedals, setShowAddMorePedals] = useState(false);
  const [availablePedals, setAvailablePedals] = useState([]);
  const [loadingPedals, setLoadingPedals] = useState(false);

  const allSelectedPedalIds = [fromPedalId, ...additionalPedalIds];

  const [matchedPedalData, setMatchedPedalData] = useState(null);

  const { marketTiers } = usePedalMarketValues(availablePedals);

  useEffect(() => {
    if (!showAddMorePedals) return;
    const loadUserPedals = async () => {
      const user = auth.currentUser;
      if (!user) return;
      setLoadingPedals(true);
      try {
        const matchedSnap = await getDoc(doc(db, 'listings', fromPedalId));
        if (matchedSnap.exists()) {
          setMatchedPedalData({ id: matchedSnap.id, ...matchedSnap.data() });
        }

        const q = query(
          collection(db, 'listings'),
          where('ownerId', '==', user.uid),
          where('active', '==', true)
        );
        const snap = await getDocs(q);
        const pedals = [];
        snap.forEach(d => {
          const data = d.data();
          if (!data.inTrade && d.id !== fromPedalId && offerModeOf(data) !== 'sell') {
            pedals.push({ id: d.id, ...data });
          }
        });
        setAvailablePedals(pedals);
      } catch (err) {
        console.error('Error loading user pedals:', err);
      } finally {
        setLoadingPedals(false);
      }
    };
    loadUserPedals();
  }, [showAddMorePedals, fromPedalId]);

  useEffect(() => {
    if (onAdditionalPedalsChange) {
      const additionalPedals = additionalPedalIds
        .map(id => availablePedals.find(pedal => pedal.id === id))
        .filter(Boolean);
      const additionalPrices = additionalPedalIds.map(id => {
        const p = availablePedals.find(pedal => pedal.id === id);
        return p ? Number(p.price) : 0;
      });
      // Third arg carries the full docs so the parent can price them against
      // the market, which needs make/model/condition, not just a price.
      onAdditionalPedalsChange(additionalPedalIds, additionalPrices, additionalPedals);
    }
  }, [additionalPedalIds, availablePedals]);

  const toggleAdditionalPedal = (pedalId) => {
    setAdditionalPedalIds(prev => {
      if (prev.includes(pedalId)) {
        return prev.filter(id => id !== pedalId);
      }
      if (prev.length >= MAX_INITIATOR_PEDALS - 1) return prev;
      return [...prev, pedalId];
    });
  };


  // Utility to check if address is complete
  const isAddressComplete = (address) => {
    if (!address) return false;
    const required = ['name', 'street1', 'city', 'state', 'zip', 'country'];
    return required.every(field => address[field] && address[field].trim());
  };

  // Auto-open the inline address modal when the user lands here without a
  // complete address. Same trigger as the original "Shipping Address Required"
  // modal — the difference is that the modal now contains the address form
  // itself instead of redirecting to /profile.
  useEffect(() => {
    if (!userLoading && !isAddressComplete(shippingAddress)) {
      setShowAddressModal(true);
    } else {
      setShowAddressModal(false);
    }
  }, [shippingAddress, userLoading]);

  // Re-evaluate UserContext on mount and when shippingAddress changes
  useEffect(() => {
    setRefreshKey(prev => prev + 1); // force re-render if shippingAddress changes
  }, [userContext.shippingAddress]);

  // Manual refresh handler
  const handleManualRefresh = async () => {
    try {
      refreshUserData();
      setShowRefreshButton(false);
      setRefreshKey(prev => prev + 1);
      console.log('[TradeOfferForm] Manual UserContext refresh completed');
    } catch (err) {
      setShowRefreshButton(true);
      console.error('[TradeOfferForm] Manual refresh failed:', err);
    }
  };

  const calculateCostBreakdown = (amount, type) => {
    if (!type) {
      setCostBreakdown(null);
      setShowCostBreakdown(false);
      return;
    }

    setCalculatingCosts(true);
    
    try {
      const cashValue = parseFloat(amount);
      const tradeCompletionFee = getTradeCompletionFee(allSelectedPedalIds.length);
      const cashFeePercentage = getMembershipCashFeePercentage();
      
      let totalYouPay = 0;
      let totalYouReceive = 0;
      let cashExchangeFee = 0;
      
      if (type === 'offer') {
        cashExchangeFee = Math.round(cashValue * (cashFeePercentage / 100) * 100) / 100;
        totalYouPay = cashValue + tradeCompletionFee + cashExchangeFee;
      } else if (type === 'ask') {
        cashExchangeFee = 0;
        totalYouReceive = cashValue - tradeCompletionFee;
      } else {
        totalYouPay = tradeCompletionFee;
      }
      
      setCostBreakdown({
        cashValue,
        tradeCompletionFee,
        pedalCount: allSelectedPedalIds.length,
        cashExchangeFee,
        cashFeePercentage,
        totalYouPay,
        totalYouReceive,
        offerType: type
      });
      setShowCostBreakdown(true);
    } catch (error) {
      console.error('Error calculating cost breakdown:', error);
    } finally {
      setCalculatingCosts(false);
    }
  };

  useEffect(() => {
    if (!offerType) {
      setCostBreakdown(null);
      setShowCostBreakdown(false);
      return;
    }
    const amount = parseFloat(cashAmount) || 0;
    const debounceTimer = setTimeout(() => {
      calculateCostBreakdown(amount, offerType);
    }, 500);
    return () => clearTimeout(debounceTimer);
  }, [cashAmount, offerType, membershipTier, allSelectedPedalIds.length]);

  const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  setSuccessMsg('');
  setErrorMsg('');

  // Block if address is incomplete
  if (!isAddressComplete(shippingAddress)) {
    setShowAddressModal(true);
    setLoading(false);
    return;
  }

  const timer = createOfferSubmitTimer(
    matchId ? 'match_trade_offer' : 'trade_offer_form',
    { listingId: toPedalId, matchId: matchId || null }
  );

  try {
    const fromUser = auth.currentUser;
    if (!fromUser) throw new Error('You must be logged in to make an offer');
    
    // ✅ VALIDATION: Sanitize and validate cash amount
    let validatedCashAmount = 0;
    if (['ask', 'offer'].includes(offerType)) {
      const rawAmount = Number(cashAmount);
      if (isNaN(rawAmount) || rawAmount < 0) {
        throw new Error('Please enter a valid cash amount (must be positive)');
      }
      if (rawAmount > 2000) {
        throw new Error('Cash amount cannot exceed $2,000');
      }
      validatedCashAmount = Math.abs(rawAmount);
      
      // Round to 2 decimal places to prevent precision issues
      validatedCashAmount = Math.round(validatedCashAmount * 100) / 100;
    }

    const safeNote = note.trim().slice(0, 500);

    const listingSnaps = await Promise.all([
      getDoc(doc(db, 'listings', toPedalId)),
      ...allSelectedPedalIds.map((pedalId) => getDoc(doc(db, 'listings', pedalId))),
    ]);
    const targetPedalDoc = listingSnaps[0];
    const initiatorPedalDocs = listingSnaps.slice(1);

    if (!targetPedalDoc.exists()) {
      throw new Error('Pedal listing not found');
    }

    const pedalData = targetPedalDoc.data();
    const currentOwnerId = pedalData.ownerId;
    if (!currentOwnerId) {
      throw new Error('Pedal has no owner');
    }

    // 🔒 CRITICAL: Double-check that target pedal is not in trade
    if (pedalData.inTrade) {
      throw new Error('This pedal is no longer available for trade offers. It may have been accepted in another trade.');
    }
    if (!allowsTrade(pedalData)) {
      throw new Error('This pedal is for sale only. The owner is not taking trade offers on it.');
    }

    const initiatorPedalPrices = {};
    const initiatorPedalDetails = {};
    const initiatorListingSnapshots = [];
    for (let i = 0; i < allSelectedPedalIds.length; i++) {
      const pedalId = allSelectedPedalIds[i];
      const initiatorPedalDoc = initiatorPedalDocs[i];
      if (!initiatorPedalDoc.exists()) {
        throw new Error('One of your selected pedals no longer exists');
      }
      const pd = initiatorPedalDoc.data();
      if (pd.inTrade) {
        throw new Error(`Your ${pd.make} ${pd.model} is no longer available. It may have been accepted in another trade.`);
      }
      initiatorPedalPrices[pedalId] = Number(pd.price || 0);
      initiatorPedalDetails[pedalId] = {
        name: `${pd.make} ${pd.model}`,
        imageUrl: pd.images?.top || null,
        placeholderUrl: pd.images?.top ? null : placeholderFor(pd).url,
        condition: pd.condition || null,
      };
      initiatorListingSnapshots.push({ id: pedalId, ...pd });
    }

    timer.mark('freshness_reads', { pedalCount: allSelectedPedalIds.length });

    const responderPedalDetail = {
      name: `${pedalData.make} ${pedalData.model}`,
      imageUrl: pedalData.images?.top || null,
      placeholderUrl: pedalData.images?.top ? null : placeholderFor(pedalData).url,
      condition: pedalData.condition || null,
    };

    const pedalSnapshots = buildPedalSnapshotsPayload(
      initiatorListingSnapshots,
      { id: toPedalId, ...pedalData }
    );

    const tradeId = await addTradeOffer({
      matchId,
      initiatorId: fromUser.uid,
      initiatorEmail: fromUser.email,
      responderId: currentOwnerId,
      initiatorPedalIds: allSelectedPedalIds,
      responderPedalId: toPedalId,
      cashAdjustmentType: offerType,
      cashAdjustmentAmount: validatedCashAmount,
      note: safeNote,
      initiatorPedalPrices,
      responderPedalPrice: Number(pedalData.price || 0),
      initiatorPedalDetails,
      responderPedalDetail,
      pedalSnapshots,
    });

    timer.mark('write_ack', { tradeOfferId: tradeId });

    void recordOfferIntentEvent(OFFER_INTENT_KINDS.TRADE_OFFER_SUBMITTED, {
      listingId: toPedalId,
      tradeOfferId: tradeId,
      matchId: matchId || null,
      source: matchId ? 'match_trade_offer' : 'trade_offer_form',
    });

    // Contextual soft-ask for push permission (native-only, no-op on web).
    maybePromptForPush('make_offer');

    timer.mark('done');
    navigate(`/offer/${tradeId}`, { state: { justCreated: true } });
  } catch (err) {
    timer.fail(err);
    console.error('Error sending offer:', err);
    setErrorMsg(err.message || 'Failed to send offer. Please try again.');
  }

  setLoading(false);
};


  return (
    <>
      <style>{`
        input[type="number"]::-webkit-outer-spin-button,
        input[type="number"]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
      `}</style>
      <form onSubmit={handleSubmit} className="space-y-4 p-4 border rounded-xl max-w-md" key={refreshKey} style={{ boxSizing: 'border-box', width: '100%' }}>
      <div style={{ marginBottom: '0.75rem' }}>
        <label className="block font-semibold" style={{ marginBottom: '8px', textAlign: 'left' }}>Offer Type:</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {[
            { value: 'straight', label: 'Straight Trade', desc: 'No cash involved' },
            { value: 'ask', label: 'Ask for additional $', desc: 'They pay you cash' },
            { value: 'offer', label: 'Offer additional $', desc: 'You pay them cash' }
          ].map(opt => (
            <label
              key={opt.value}
              onClick={() => setOfferType(opt.value)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px', borderRadius: '10px', cursor: 'pointer',
                border: offerType === opt.value ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                backgroundColor: offerType === opt.value ? '#eff6ff' : '#f9fafb',
                transition: 'all 0.15s'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="radio"
                  name="offerType"
                  value={opt.value}
                  checked={offerType === opt.value}
                  onChange={(e) => setOfferType(e.target.value)}
                  style={{ flexShrink: 0, accentColor: '#3b82f6' }}
                />
                <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#111827' }}>{opt.label}</span>
              </div>
              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{opt.desc}</span>
            </label>
          ))}
        </div>
      </div>

      {['ask', 'offer'].includes(offerType) && (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ fontWeight: '500', fontSize: '0.85rem', color: '#374151', whiteSpace: 'nowrap' }}>Amount ($)</label>
            <input
              type="text"
              inputMode="decimal"
              className="border border-gray-300 focus:border-blue-500 focus:outline-none"
              style={{ flex: 1, padding: '8px 12px', fontSize: '0.95rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}
              value={cashAmount}
              onChange={(e) => setCashAmount(formatCurrencyInput(e.target.value))}
              placeholder="0"
              required
            />
          </div>
        </div>
      )}

      {/* Cost Breakdown */}
      {calculatingCosts && (
        <div className="text-center text-sm text-gray-500 py-2">
          Calculating costs...
        </div>
      )}
      
      {showCostBreakdown && costBreakdown && !calculatingCosts && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ borderBottom: '1px solid #e5e7eb', marginBottom: '12px' }} />
          <h5 style={{ fontWeight: '600', fontSize: '0.95rem', color: '#111827', marginTop: 0, marginBottom: '10px', textAlign: 'left' }}>Your cost breakdown</h5>
          <div style={{ fontSize: '0.85rem', color: '#374151' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span>Platform fee</span>
              <span>
                ${costBreakdown.tradeCompletionFee.toFixed(2)}
                {costBreakdown.pedalCount > 1 && (
                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}> ($3 + $1/addtl pedal)</span>
                )}
              </span>
            </div>
            {costBreakdown.offerType === 'offer' && costBreakdown.cashValue > 0 && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span>Cash payment</span>
                  <span>${costBreakdown.cashValue.toFixed(2)}</span>
                </div>
                {costBreakdown.cashExchangeFee > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', color: '#6b7280' }}>
                    <span>Cash processing fee ({costBreakdown.cashFeePercentage}%)</span>
                    <span>${costBreakdown.cashExchangeFee.toFixed(2)}</span>
                  </div>
                )}
              </>
            )}
            {costBreakdown.offerType === 'ask' && costBreakdown.cashValue > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', color: '#16a34a' }}>
                <span>Cash you'll receive</span>
                <span>+${costBreakdown.cashValue.toFixed(2)}</span>
              </div>
            )}
            {(() => {
              const netReceive = costBreakdown.offerType === 'ask' && costBreakdown.totalYouReceive > 0;
              const displayAmount = netReceive
                ? costBreakdown.totalYouReceive
                : (costBreakdown.offerType === 'ask' ? Math.abs(costBreakdown.totalYouReceive) : costBreakdown.totalYouPay);
              return (
                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '0.95rem' }}>
                  <span>Total you'll {netReceive ? 'receive' : 'pay'}</span>
                  <span style={{ color: netReceive ? '#15803d' : '#111827' }}>
                    {netReceive ? '+' : ''}${displayAmount.toFixed(2)}
                  </span>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Add More Pedals Section */}
      <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
        {!showAddMorePedals ? (
          <button
            type="button"
            onClick={() => setShowAddMorePedals(true)}
            style={{
              backgroundColor: 'transparent', border: '1px solid #5A9ECB', borderRadius: '6px',
              padding: '8px 16px', color: '#5A9ECB', fontSize: '0.95rem', fontWeight: '500',
              cursor: 'pointer', fontFamily: 'Nunito, sans-serif', width: '100%',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => { e.target.style.backgroundColor = '#5A9ECB'; e.target.style.color = 'white'; }}
            onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = '#5A9ECB'; }}
          >
            + Add additional pedals from your pedalboard to the offer
          </button>
        ) : (
          <div data-swipe-back-opt-out="true" style={{ border: '1.5px solid #9ca3af', borderRadius: '8px', padding: '12px' }}>
            {/* data-swipe-back-opt-out: scrolling this panel's pedal grid with
                a slightly arced thumb read as a rightward swipe and triggered
                the global swipe-back mid-selection on /match and /offer
                (2026-10-02). Gestures born inside this box belong to the box;
                see startedInOptOut in hooks/useSwipeViews.js. */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontWeight: '600', fontSize: '0.85rem', color: '#374151' }}>
                Your pedals — add up to {MAX_INITIATOR_PEDALS - 1 - additionalPedalIds.length} more
              </span>
              <button
                type="button"
                onClick={() => { setShowAddMorePedals(false); setAdditionalPedalIds([]); }}
                style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '0.8rem' }}
              >
                Close
              </button>
            </div>
            {loadingPedals ? (
              <p style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Loading your pedals...</p>
            ) : (
              <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                  {/* Matched pedal - locked as #1 */}
                  {matchedPedalData && (
                    <div style={{
                      position: 'relative', textAlign: 'center', padding: '6px',
                      borderRadius: '8px', border: '2px solid #3b82f6',
                      backgroundColor: '#EFF6FF', opacity: 0.85
                    }}>
                      <div style={{
                        position: 'absolute', top: '2px', left: '2px', zIndex: 2,
                        backgroundColor: '#3b82f6', color: 'white', borderRadius: '50%',
                        width: '18px', height: '18px', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: '0.6rem', fontWeight: 'bold'
                      }}>1</div>
                      <div style={{
                        position: 'absolute', top: '2px', right: '2px', zIndex: 2,
                        fontSize: '0.55rem', color: '#6b7280', backgroundColor: 'rgba(255,255,255,0.9)',
                        padding: '1px 4px', borderRadius: '4px'
                      }}>Matched</div>
                      <img
                        src={matchedPedalData.images?.top || placeholderFor(matchedPedalData).url}
                        alt={`${matchedPedalData.make} ${matchedPedalData.model}`}
                        style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '6px', opacity: matchedPedalData.images?.top ? 1 : 0.6 }}
                      />
                      <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#374151', marginTop: '4px', lineHeight: '1.2' }}>{matchedPedalData.make}</div>
                      <div style={{ fontSize: '0.65rem', color: '#6b7280', lineHeight: '1.2' }}>{matchedPedalData.model}</div>
                    </div>
                  )}
                  {/* Additional pedals to select (selected reorder to top, like Direct Offer) */}
                  {[...availablePedals].sort((a, b) => {
                    const aIdx = additionalPedalIds.indexOf(a.id);
                    const bIdx = additionalPedalIds.indexOf(b.id);
                    if (aIdx !== -1 && bIdx === -1) return -1;
                    if (aIdx === -1 && bIdx !== -1) return 1;
                    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
                    return 0;
                  }).map(pedal => {
                    const isSelected = additionalPedalIds.includes(pedal.id);
                    const isMaxed = additionalPedalIds.length >= MAX_INITIATOR_PEDALS - 1 && !isSelected;
                    const selectionNum = isSelected ? allSelectedPedalIds.indexOf(pedal.id) + 1 : null;
                    return (
                      <div
                        key={pedal.id}
                        onClick={() => !isMaxed && toggleAdditionalPedal(pedal.id)}
                        style={{
                          position: 'relative', cursor: isMaxed ? 'not-allowed' : 'pointer',
                          opacity: isMaxed ? 0.5 : 1, textAlign: 'center', padding: '6px',
                          borderRadius: '8px', border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                          backgroundColor: isSelected ? '#EFF6FF' : 'white',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        {isSelected && (
                          <div style={{
                            position: 'absolute', top: '2px', left: '2px', zIndex: 2,
                            backgroundColor: '#3b82f6', color: 'white', borderRadius: '50%',
                            width: '18px', height: '18px', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', fontSize: '0.6rem', fontWeight: 'bold'
                          }}>
                            {selectionNum}
                          </div>
                        )}
                        <img
                          src={pedal.images?.top || placeholderFor(pedal).url}
                          alt={`${pedal.make} ${pedal.model}`}
                          style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '6px', opacity: pedal.images?.top ? 1 : 0.6 }}
                        />
                        <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#374151', marginTop: '4px', lineHeight: '1.2' }}>{pedal.make}</div>
                        <div style={{ fontSize: '0.65rem', color: '#6b7280', lineHeight: '1.2' }}>{pedal.model}</div>
                        {(() => {
                          const displayTier = getDisplayTier(marketTiers.get(pedal.id));
                          if (!displayTier) return null;
                          return (
                            <div style={{ fontSize: '0.6rem', color: '#9ca3af', lineHeight: '1.2', marginTop: '2px' }}>
                              Market ~{formatDollarsFromCents(displayTier.mid)}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mb-4" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', boxSizing: 'border-box' }}>
        <div style={{ marginBottom: '0.5rem', width: '100%', textAlign: 'left' }}>
          <label className="font-semibold">Note (optional):</label>
        </div>
        <textarea
          className="w-full border border-gray-300 focus:border-blue-500 focus:outline-none resize-none"
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a short note..."
          disabled={loading}
          rows={6}
          style={{ padding: '12px', fontSize: '16px', borderRadius: '6px', lineHeight: '1.5', width: '100%', boxSizing: 'border-box' }}
        />
      </div>

      {!userLoading && !isAddressComplete(shippingAddress) && (
        <p style={{ color: '#dc2626', fontSize: '0.95rem', marginTop: '12px', marginBottom: '0', lineHeight: '1.4', textAlign: 'center' }}>
          A shipping address is required before submitting this offer.{' '}
          <button
            type="button"
            onClick={() => setShowAddressModal(true)}
            style={{ color: '#dc2626', textDecoration: 'underline', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 'inherit', fontFamily: 'inherit' }}
          >
            Add address
          </button>
        </p>
      )}

      {/* Heads-up when the other owner hasn't been on in a while (30+
          days by their last active week; see utils/inactivity). The parent
          (MatchDetails) passes the owner's public profile fields. */}
      <div style={{ marginTop: '16px' }}>
        <InactiveOwnerNotice ownerName={ownerName} lastActiveWeek={ownerLastActiveWeek} anchorRef={submitButtonRef} />
      </div>

      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '10px',
        marginTop: '16px'
      }}>
        <button
          ref={submitButtonRef}
          type="submit"
          style={{
            backgroundColor: loading || !offerType || !isAddressComplete(shippingAddress) ? '#9CA3AF' : '#C9A227',
            color: 'white',
            border: 'none',
            borderRadius: '9999px',
            padding: '12px 24px',
            fontFamily: 'Nunito, sans-serif',
            fontWeight: 'bold',
            fontSize: '1rem',
            cursor: loading || !offerType || !isAddressComplete(shippingAddress) ? 'not-allowed' : 'pointer',
            opacity: loading || !offerType || !isAddressComplete(shippingAddress) ? 0.6 : 1,
            width: '100%'
          }}
          disabled={loading || !offerType || !isAddressComplete(shippingAddress)}
          onMouseEnter={(e) => {
            if (!loading && offerType && isAddressComplete(shippingAddress)) {
              e.target.style.opacity = '0.85';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading && offerType && isAddressComplete(shippingAddress)) {
              e.target.style.opacity = '1';
            }
          }}
        >
          {loading ? 'Sending...' : 'Send Offer'}
        </button>

        <button
          type="button"
          onClick={() => {
            if (onCancel) {
              onCancel();
            }
          }}
          style={{
            backgroundColor: 'transparent',
            color: '#374151',
            border: '1.5px solid #d1d5db',
            borderRadius: '9999px',
            padding: '12px 24px',
            fontFamily: 'Nunito, sans-serif',
            fontWeight: '600',
            fontSize: '1rem',
            cursor: 'pointer',
            width: '100%'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        >
          Cancel
        </button>
      </div>

      {successMsg && <p className="text-green-600 mt-2">{successMsg}</p>}
      {errorMsg && <p className="text-red-600 mt-2">{errorMsg}</p>}
      {showRefreshButton && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
          <p className="text-sm text-blue-800 mb-2">
            If you just updated your address and are still blocked, click below to refresh your session.
          </p>
          <button
            onClick={handleManualRefresh}
            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
          >
            Refresh Session
          </button>
        </div>
      )}
    </form>

    {/* Inline shipping-address modal: opens only when the user clicks
        Send Offer without a complete address. */}
    <AddressRequiredModal
      isOpen={showAddressModal}
      onClose={() => setShowAddressModal(false)}
      onSaved={() => setShowAddressModal(false)}
      user={userContext?.firestoreUser || { shippingAddress }}
      context="match-offer"
    />
    </>
  );
};

export default TradeOfferForm;